import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getModerationProducts } from '../redux/reducers/utils/moderationReducers/moderationProductsReducer';
import { getModerationReviews } from '../redux/reducers/utils/moderationReducers/moderationReviewsReducer';
import { checkAccess } from '../core/utils';
import dayjs from 'dayjs';

export const useModerationProductsCount = () => {
  const dispatch = useDispatch();
  const { accessComponentsForUser } = useSelector((store) => store.services);
  const { currentModule } = useSelector((store) => store.nav);
  const moderationProductsAccess = checkAccess(accessComponentsForUser, 'moderation/products');
  const moderationReviewsAccess = checkAccess(accessComponentsForUser, 'moderation/reviews');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProductsCount = useCallback(async () => {
    if (!moderationProductsAccess) {
      return null;
    }

    try {
      await dispatch(getModerationProducts({ offset: 0, limit: 40 })).unwrap();
      return null;
    } catch (err) {
      return err;
    }
  }, [dispatch, moderationProductsAccess]);

  const fetchReviewsCount = useCallback(async () => {
    if (!moderationReviewsAccess) {
      return null;
    }

    try {
      await dispatch(
        getModerationReviews({
          offset: 0,
          limit: 50,
          ratings: [1, 2],
          // adminStatuses: [1, 2],
          reviewCreatedFrom: dayjs().subtract(14, 'day').format('YYYY-MM-DD'),
          reviewCreatedTo: dayjs().format('YYYY-MM-DD'),
        }),
      ).unwrap();
      return null;
    } catch (err) {
      return err;
    }
  }, [dispatch, moderationReviewsAccess]);

  const fetchCount = useCallback(async () => {
    if (!moderationProductsAccess && !moderationReviewsAccess) {
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    const [productsError, reviewsError] = await Promise.all([
      fetchProductsCount(),
      fetchReviewsCount(),
    ]);

    setError(productsError || reviewsError || null);
    setLoading(false);
  }, [fetchProductsCount, fetchReviewsCount, moderationProductsAccess, moderationReviewsAccess]);
  
  useEffect(() => {
    // Не опрашиваем данные, если у пользователя нет доступа
    // или если мы находимся в модуле модерации (чтобы не мешать работе страницы)
    if ((!moderationProductsAccess && !moderationReviewsAccess) || currentModule === 'moderation') {
      setLoading(false);
      setError(null);
      return;
    }

    fetchCount();
    const interval = setInterval(fetchCount, 60 * 1000);

    return () => clearInterval(interval);
  }, [fetchCount, moderationProductsAccess, moderationReviewsAccess, currentModule]);

  return { loading, error, refreshModerationData: fetchCount };
};